import { doc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { User } from '@/types/users';

/**
 * Generate a secure random token for invitations
 */
const generateSecureToken = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
};

export interface InviteResult {
  success: boolean;
  message: string;
  invitationId?: string;
  token?: string;
  inviteUrl?: string;
}





/**
 * Create an invitation record in Firestore
 * Note: We do NOT create Firebase Auth users from the frontend for security reasons
 */
const createInvitationRecord = async (
  user: User, 
  workspace: { id: string; name: string; slug: string },
  method: 'whatsapp' | 'email' | 'sms',
  inviter: { id: string; email: string; displayName: string },
  role: string
): Promise<{ invitationId: string; token: string; inviteUrl: string }> => {
  try {
    // Generate secure random token
    const token = generateSecureToken();
    
    // Create invitation data
    const invitationData = {
      userId: user.id,
      invitedUserId: user.id, // Required by security rules
      userEmail: user.email,
      userPhone: user.phoneNumber,
      userDisplayName: user.displayName,
      workspace: {
        id: workspace.id,
        name: workspace.name,
        slug: workspace.slug
      },
      role,
      inviteMethod: method,
      inviter: {
        id: inviter.id,
        email: inviter.email,
        displayName: inviter.displayName
      },
      token,
      status: 'pending',
      invitedAt: new Date(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
      attempts: 0,
      language: user.language || 'en'
    };
    
    console.log('🔍 Debug - Invitation Data to Create:', invitationData);
    console.log('🔍 Debug - Using token as document ID:', token);
    
    // Use token as document ID for better security
    const invitationsRef = doc(db, 'invitations', token);
    await setDoc(invitationsRef, invitationData);
    
    // Generate invite URL
    const inviteUrl = `${window.location.origin}/invite?token=${token}`;
    
    return {
      invitationId: token, // Use token as ID
      token,
      inviteUrl
    };
  } catch (error) {
    console.error('Error creating invitation record:', error);
    throw new Error('Failed to create invitation record');
  }
};

/**
 * Update user document with invite information
 */
const updateUserInviteStatus = async (userId: string, invited: boolean = true): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { 
      invited,
      invitedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating user invite status:', error);
    throw new Error('Failed to update user invite status');
  }
};

/**
 * Main invite function that handles the complete workflow
 */
export const inviteUser = async (
  user: User, 
  method: 'whatsapp' | 'email' | 'sms',
  firestoreUser: User,
  role: string = 'resident'
): Promise<InviteResult> => {
  try {
    // Debug: Log current user data to understand permissions
    console.log('🔍 Debug - Current User Data:', {
      userId: firestoreUser.id,
      email: firestoreUser.email,
      roles: firestoreUser.roles,
      workspace: firestoreUser.workspace,
      hasWorkspace: !!firestoreUser.workspace?.id
    });

    // Validate user has required contact information
    if (!user.email) {
      throw new Error('User must have an email address to be invited');
    }

    if (method === 'whatsapp' && !user.phoneNumber) {
      throw new Error('User must have a phone number for WhatsApp invitation');
    }

    // Validate current user has required permissions
    if (!firestoreUser.workspace?.id) {
      throw new Error('Current user must be associated with a workspace');
    }

    // Check if user has admin role
    const hasAdminRole = firestoreUser.roles && (
      firestoreUser.roles.includes('administrator') || 
      firestoreUser.roles.includes('Root user')
    );
    
    if (!hasAdminRole) {
      console.error('❌ User roles:', firestoreUser.roles);
      throw new Error('Current user must have administrator role to send invitations');
    }

    // Note: We no longer check for existing users or create Firebase Auth users from frontend
    // This is a security measure to prevent unauthorized user creation
    
    // For security reasons, we do NOT create Firebase Auth users from the frontend
    // Instead, we create an invitation record and let the user set up their own account
    
    // Create invitation record
    const invitationResult = await createInvitationRecord(
      user, 
      { 
        id: firestoreUser.workspace?.id || 'unknown', 
        name: firestoreUser.workspace?.name || 'Unknown Workspace', 
        slug: firestoreUser.workspace?.slug || 'unknown' 
      }, 
      method,
      {
        id: firestoreUser.id,
        email: firestoreUser.email,
        displayName: firestoreUser.displayName
      },
      role
    );
    
    // Update user document with invite status
    await updateUserInviteStatus(user.id, true);
    
    return {
      success: true,
      message: `User has been invited to access the workspace "${firestoreUser.workspace?.name || 'Unknown Workspace'}". They can use the invite link to create their account.`,
      invitationId: invitationResult.invitationId,
      token: invitationResult.token,
      inviteUrl: invitationResult.inviteUrl
    };
  } catch (error) {
    console.error('Error inviting user:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to invite user'
    };
  }
};

/**
 * Generate invite message based on method and user status
 */
export const generateInviteMessage = (
  user: User,
  workspaceName: string,
  inviteUrl: string
): string => {
  const baseMessage = `Hi ${user.displayName || user.username || 'there'}!\n\n`;
  
  const message = `${baseMessage}You have been invited to access the workspace "${workspaceName}".\n\nClick the link below to create your account:\n${inviteUrl}\n\nWorkspace: ${workspaceName}\n\nWe look forward to having you join our team!`;
  
  return message;
};

/**
 * Generate WhatsApp invite URL
 */
export const generateWhatsAppUrl = (phoneNumber: string, message: string): string => {
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
};

/**
 * Generate email invite URL
 */
export const generateEmailUrl = (email: string, subject: string, body: string): string => {
  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);
  return `mailto:${email}?subject=${encodedSubject}&body=${encodedBody}`;
};

/**
 * Generate SMS invite URL
 */
export const generateSMSUrl = (phoneNumber: string, message: string): string => {
  const encodedMessage = encodeURIComponent(message);
  return `sms:${phoneNumber}?body=${encodedMessage}`;
};
